import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import api from "../lib/api";
import { useVisitor } from "../context/VisitorAuthContext";
import { toast } from "sonner";
import { Trophy, Settings, LogOut, ArrowLeft, Sparkles } from "lucide-react";

export default function Account() {
  const { visitor, isMember, refresh, refreshAccess, openPortal, logout, openAuth } = useVisitor();
  const [params, setParams] = useSearchParams();
  const [verifying, setVerifying] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const nav = useNavigate();

  // Confirm a just-completed Stripe Checkout (success_url carries ?session_id=).
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    setVerifying(true);
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          await refresh(); await refreshAccess();
          toast.success("Welcome to the Club! 🎉");
          setVerifying(false);
          setParams({}, { replace: true });
          return;
        }
      } catch {}
      if (tries < 8) setTimeout(poll, 1500);
      else { setVerifying(false); setParams({}, { replace: true }); toast.info("Payment is processing — refresh in a moment."); }
    };
    poll();
    // eslint-disable-next-line
  }, []);

  if (visitor === false) {
    return (
      <Layout>
        <div className="font-ui bg-white min-h-screen border-t border-[#E5E5E5] flex items-center justify-center py-24">
          <div className="text-center">
            <p className="font-ui text-slate-600 mb-4">Sign in to view your account.</p>
            <button onClick={openAuth} className="bg-[#C5A059] text-black font-pub font-extrabold px-6 py-3 rounded-xl border-2 border-black shadow-[0_3px_10px_rgba(26,22,21,0.14)] uppercase tracking-wide text-sm">Sign in</button>
          </div>
        </div>
      </Layout>
    );
  }

  const portal = async () => {
    setPortalBusy(true);
    try { await openPortal(); }
    catch { toast.error("No subscription found to manage."); setPortalBusy(false); }
  };

  return (
    <Layout>
      <div className="font-ui bg-white min-h-screen border-t border-[#E5E5E5]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <Link to="/trivia" className="inline-flex items-center gap-1.5 font-ui text-sm font-bold text-slate-500 hover:text-black mb-6"><ArrowLeft className="w-4 h-4" /> Back to the Vault</Link>
          <h1 className="font-pub text-3xl sm:text-4xl font-extrabold text-black mb-6">Your account</h1>

          {verifying && (
            <div data-testid="account-verifying" className="bg-[#F3EAD6] border-2 border-[#C5A059] rounded-2xl p-5 mb-5 font-ui text-sm font-bold text-black">Confirming your subscription…</div>
          )}

          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6">
            <p className="font-ui text-xs uppercase tracking-wider font-bold text-slate-400">Signed in as</p>
            <p className="font-pub text-lg font-bold text-black mt-1" data-testid="account-email">{visitor?.email}</p>
          </div>

          <div className="bg-white border-2 border-[#C5A059] rounded-2xl p-6 mt-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-ui text-xs uppercase tracking-wider font-bold text-slate-400">Membership</p>
                <p data-testid="account-status" className="font-pub text-2xl font-extrabold text-black mt-1 flex items-center gap-2">
                  {isMember ? <><Trophy className="w-5 h-5 text-[#A5812F]" /> Club member</> : "Free reader"}
                </p>
                {isMember && visitor?.plan && <p className="font-ui text-sm text-slate-500 mt-1">Plan: {visitor.plan.replace("club_", "")}</p>}
              </div>
            </div>
            {isMember ? (
              <button onClick={portal} disabled={portalBusy} data-testid="manage-subscription" className="mt-5 inline-flex items-center gap-2 bg-black hover:bg-slate-800 text-[#C5A059] font-pub font-extrabold px-6 py-3 rounded-xl transition-all uppercase tracking-wide text-sm disabled:opacity-50">
                <Settings className="w-4 h-4" /> {portalBusy ? "Opening…" : "Manage / cancel subscription"}
              </button>
            ) : (
              <button onClick={() => nav("/club")} data-testid="account-join-club" className="mt-5 inline-flex items-center gap-2 bg-[#C5A059] hover:bg-[#B08D44] text-black font-pub font-extrabold px-6 py-3 rounded-xl border-2 border-black shadow-[0_3px_10px_rgba(26,22,21,0.14)] uppercase tracking-wide text-sm">
                <Sparkles className="w-4 h-4" /> Join the Club
              </button>
            )}
          </div>

          <button onClick={() => { logout(); nav("/"); }} data-testid="account-logout" className="mt-6 inline-flex items-center gap-2 font-ui text-sm font-bold text-slate-400 hover:text-[#8A1E1E]">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>
    </Layout>
  );
}
